import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { Form, FormField, FormFieldType, FormOption } from '../fixtures/domain.js';
import {
  canAddField,
  canAddOption,
  isSendable,
  sameForm,
  MAX_FIELDS,
  MAX_FORM_NAME,
  MAX_LABEL,
  MAX_OPTION_LABEL,
  MAX_OPTIONS,
  MIN_OPTIONS,
} from '../data/forms.js';
import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import styles from './FormEditor.module.css';

function blankOption(): FormOption {
  return { id: crypto.randomUUID(), label: '' };
}

function blankField(): FormField {
  // Two options from the start: two is what makes it a question (MIN_OPTIONS).
  return {
    id: crypto.randomUUID(),
    label: '',
    type: 'choice',
    options: [blankOption(), blankOption()],
  };
}

export function blankForm(): Form {
  return {
    id: crypto.randomUUID(),
    name: '',
    fields: [blankField()],
    updatedAt: new Date().toISOString(),
  };
}

/**
 * The builder: the preview with the words made editable. Validation waits for
 * a Save press — pressing it on an incomplete draft marks every empty field
 * where it is and focuses the first, and the marks stay live after.
 * Controls at their caps are withheld, not disabled (five questions is the
 * feature, not a setting to find).
 */
export function FormEditor({
  initial,
  saving,
  maxFields = MAX_FIELDS,
  templatesOpen,
  onToggleTemplates,
  onDirtyChange,
  onSave,
  onCancel,
}: {
  initial: Form;
  saving: boolean;
  /** The question cap the session serves (`Limits.formMaxFields`). */
  maxFields?: number;
  /** Whether the templates panel is up, and the switch for it — the panel is
   *  the section's, so every screen in the section carries its switch. */
  templatesOpen?: boolean;
  onToggleTemplates?: () => void;
  /**
   * Told when the draft moves off what it was handed, and back. The draft
   * stays in here; what the page needs is one bit — whether a template
   * landing on this form would cost anything (see FormTemplatesPanel).
   */
  onDirtyChange?: (dirty: boolean) => void;
  onSave: (form: Form) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<Form>(initial);
  // The option just born from the ghost row, so the caret can follow into it.
  const [focusOptionId, setFocusOptionId] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const attempted = attempts > 0;
  const columnRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (attempts === 0) return;
    columnRef.current?.querySelector<HTMLElement>('[aria-invalid="true"]')?.focus();
  }, [attempts]);

  const dirty = !sameForm(draft, initial);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);
  // Closing the builder leaves nothing to lose; the page hears it from here
  // rather than remembering to clear the flag at each exit.
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  const complete = isSendable(draft);
  const missing = (value: string) => attempted && value.trim() === '';
  const nameMissing = missing(draft.name);
  const nameEnd = endOf(
    draft.name,
    MAX_FORM_NAME,
    nameMissing,
    <span className={styles.endPencil} aria-hidden="true">
      <Icon name="pencil" size={16} />
    </span>,
  );
  const atCap = !canAddField(draft.fields, maxFields);
  const status =
    attempted && !complete
      ? 'Fill in the marked fields to save.'
      : atCap
        ? `${maxFields} questions is the most a form can have.`
        : null;

  const save = () => {
    if (complete) {
      onSave(draft);
      return;
    }
    setAttempts((count) => count + 1);
  };

  const patchField = (id: string, change: (field: FormField) => FormField) =>
    setDraft((current) => ({
      ...current,
      fields: current.fields.map((field) => (field.id === id ? change(field) : field)),
    }));

  const addField = () =>
    setDraft((current) => ({ ...current, fields: [...current.fields, blankField()] }));

  const removeField = (id: string) =>
    setDraft((current) => ({
      ...current,
      fields: current.fields.filter((field) => field.id !== id),
    }));

  return (
    <div className={styles.editor}>
      <div className={styles.scroll}>
        <div className={styles.column} ref={columnRef}>
          <div className={styles.titleRow}>
            <Slot end={nameEnd}>
              <input
                className={inputClass(styles.name!, nameMissing, nameEnd)}
                value={draft.name}
                maxLength={MAX_FORM_NAME}
                placeholder="Untitled form"
                aria-label="Form name"
                aria-invalid={nameMissing || undefined}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, name: event.target.value }))
                }
              />
            </Slot>
            {onToggleTemplates && (
              <button
                type="button"
                className={templatesOpen ? styles.panelOn : styles.panel}
                aria-pressed={templatesOpen}
                title="Templates"
                aria-label="Templates"
                onClick={onToggleTemplates}
              >
                <Icon name={templatesOpen ? 'panelRightClose' : 'panelRight'} size={18} />
              </button>
            )}
          </div>

          <ol className={styles.fields}>
            {draft.fields.map((field, index) => {
              const labelMissing = missing(field.label);
              const labelEnd = endOf(field.label, MAX_LABEL, labelMissing);
              const markIcon = field.type === 'choice' ? 'radio' : 'tickbox';
              return (
                <li key={field.id} className={styles.field}>
                  <div className={styles.fieldHead}>
                    <Slot end={labelEnd}>
                      <input
                        className={inputClass(styles.label!, labelMissing, labelEnd)}
                        value={field.label}
                        maxLength={MAX_LABEL}
                        placeholder="Type your question here"
                        aria-label={`Question ${index + 1}`}
                        aria-invalid={labelMissing || undefined}
                        onChange={(event) =>
                          patchField(field.id, (current) => ({
                            ...current,
                            label: event.target.value,
                          }))
                        }
                      />
                    </Slot>
                    {/* Only past one: removing the last question leaves a form
                        that cannot be sent — deleting the form means that. */}
                    {draft.fields.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeField}
                        title="Remove this question"
                        aria-label={`Remove question ${index + 1}`}
                        onClick={() => removeField(field.id)}
                      >
                        <Icon name="trash" size={15} />
                      </button>
                    )}
                  </div>

                  <div className={styles.controls}>
                    <TypeToggle
                      type={field.type}
                      onChange={(type) => patchField(field.id, (current) => ({ ...current, type }))}
                    />
                  </div>

                  <ul className={styles.options}>
                    {field.options.map((option, optionIndex) => {
                      const optionMissing = missing(option.label);
                      const optionEnd = endOf(option.label, MAX_OPTION_LABEL, optionMissing);
                      return (
                        <li key={option.id} className={styles.option}>
                          <span className={styles.mark}>
                            <Icon name={markIcon} size={13} />
                          </span>
                          <Slot end={optionEnd}>
                            <input
                              className={inputClass(styles.optionInput!, optionMissing, optionEnd)}
                              value={option.label}
                              maxLength={MAX_OPTION_LABEL}
                              placeholder={`Option ${optionIndex + 1}`}
                              aria-label={`Question ${index + 1}, option ${optionIndex + 1}`}
                              aria-invalid={optionMissing || undefined}
                              ref={(element) => {
                                if (!element || focusOptionId !== option.id) return;
                                element.focus();
                                const end = element.value.length;
                                element.setSelectionRange(end, end);
                                setFocusOptionId(null);
                              }}
                              onChange={(event) =>
                                patchField(field.id, (current) => ({
                                  ...current,
                                  options: current.options.map((entry) =>
                                    entry.id === option.id
                                      ? { ...entry, label: event.target.value }
                                      : entry,
                                  ),
                                }))
                              }
                            />
                          </Slot>
                          {field.options.length > MIN_OPTIONS && (
                            <button
                              type="button"
                              className={styles.removeOption}
                              title="Remove this option"
                              aria-label={`Remove option ${optionIndex + 1}`}
                              onClick={() =>
                                patchField(field.id, (current) => ({
                                  ...current,
                                  options: current.options.filter(
                                    (entry) => entry.id !== option.id,
                                  ),
                                }))
                              }
                            >
                              <Icon name="close" size={13} />
                            </button>
                          )}
                        </li>
                      );
                    })}
                    {/* The next option as a ghost of one: it becomes real on the
                        first keystroke, with a fresh ghost under it. */}
                    {canAddOption(field.options) ? (
                      <li className={styles.optionGhost}>
                        <span className={styles.mark}>
                          <Icon name={markIcon} size={13} />
                        </span>
                        <Slot end={null}>
                          <input
                            className={styles.optionInput}
                            value=""
                            maxLength={MAX_OPTION_LABEL}
                            placeholder="Add another option"
                            aria-label={`Question ${index + 1}, add another option`}
                            onChange={(event) => {
                              const born = { ...blankOption(), label: event.target.value };
                              setFocusOptionId(born.id);
                              patchField(field.id, (current) => ({
                                ...current,
                                options: [...current.options, born],
                              }));
                            }}
                          />
                        </Slot>
                      </li>
                    ) : (
                      <li className={styles.optionCap}>
                        {MAX_OPTIONS} options is the most a question can have.
                      </li>
                    )}
                  </ul>
                </li>
              );
            })}
          </ol>

          {!atCap && (
            <button type="button" className={styles.addField} onClick={addField}>
              <Icon name="plus" size={15} />
              Add question
            </button>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        {status && <p className={styles.footnote}>{status}</p>}
        <div className={styles.footerActions}>
          <Button variant="ghost" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          {/* Always pressable: the refusal happens at the fields, not as a
              greyed button in advance. No drafts — saved whole or not at all. */}
          <Button onClick={save} disabled={saving}>
            Save form
          </Button>
        </div>
      </div>
    </div>
  );
}

/** An input and whatever sits at its right end — pencil, "Required", or the
 *  countdown — all taking the same place over the input's own end. */
function Slot({ end, children }: { end: ReactNode; children: ReactNode }) {
  return (
    <span className={styles.slot}>
      {children}
      {end && <span className={styles.end}>{end}</span>}
    </span>
  );
}

const COUNTDOWN_FROM = 10;

function endOf(value: string, max: number, missing: boolean, rest: ReactNode = null): ReactNode {
  if (missing) return <span className={styles.endRequired}>Required</span>;
  const left = max - value.length;
  if (left <= COUNTDOWN_FROM) {
    return (
      <span className={left === 0 ? styles.endFull : styles.endCount}>
        {value.length}/{max}
      </span>
    );
  }
  return rest;
}

function inputClass(base: string, invalid: boolean, end: ReactNode): string {
  return [base, invalid && styles.invalid, end && styles.hasEnd].filter(Boolean).join(' ');
}

/** Single or multiple answers: the whole of what a question can differ by. */
function TypeToggle({
  type,
  onChange,
}: {
  type: FormFieldType;
  onChange: (type: FormFieldType) => void;
}) {
  return (
    <div className={styles.types} role="group" aria-label="Answer type">
      <button
        type="button"
        className={type === 'choice' ? styles.typeOn : styles.type}
        aria-pressed={type === 'choice'}
        onClick={() => onChange('choice')}
      >
        Single
      </button>
      <button
        type="button"
        className={type === 'multiChoice' ? styles.typeOn : styles.type}
        aria-pressed={type === 'multiChoice'}
        onClick={() => onChange('multiChoice')}
      >
        Multiple answers
      </button>
    </div>
  );
}
